import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import api from '../utils/api';

const FINANCE_METHODS = ['CASH', 'GOOD LEAP', 'AQUA', 'FOUNDATION', 'LENDKEY', 'HOMERUN', 'FINAL', 'PROG'];
const PRODUCTS = ['Roofing', 'Ext Coating', 'Windows', 'Gutters', 'Other'];

const emptyRow = () => ({
  customer_name: '',
  amount_to_collect: '',
  product: '',
  finance_method: '',
  est_collection_date: '',
  collected: '',
  collected_date: '',
  payment_type: '',
  file_submitted: '',
  file_submitted_date: '',
  total_after_fees: '',
  date_in_bank: '',
  sales_rep: '',
});

const fmt = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

function getWeekLabel(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diffToMon = (day === 0 ? -6 : 1 - day);
  const mon = new Date(d);
  mon.setDate(d.getDate() + diffToMon);
  const fri = new Date(mon);
  fri.setDate(mon.getDate() + 4);
  const fmt = (dt) => dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(mon)} – ${fmt(fri)}, ${fri.getFullYear()}`;
}

function getMonday() {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

export default function Collection() {
  const [weeks, setWeeks] = useState([]);
  const [selectedWeekId, setSelectedWeekId] = useState('');
  const [currentId, setCurrentId] = useState(null);
  const [weekStart, setWeekStart] = useState(getMonday());
  const [projectManager, setProjectManager] = useState('');
  const [minGoal, setMinGoal] = useState('');
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    loadWeekList();
  }, []);

  const loadWeekList = async () => {
    try {
      const res = await api.get('/collection');
      const data = res.data !== undefined ? res.data : res;
      setWeeks(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load week list');
    }
  };

  const loadWeek = async (id) => {
    if (!id) return;
    try {
      const res = await api.get(`/collection/${id}`);
      const data = res.data !== undefined ? res.data : res;
      setCurrentId(data.id);
      setWeekStart(data.week_start);
      setProjectManager(data.project_manager || '');
      setMinGoal(data.min_goal || '');
      const parsedRows = typeof data.rows === 'string' ? JSON.parse(data.rows) : data.rows;
      setRows(parsedRows && parsedRows.length ? parsedRows : [emptyRow()]);
      setDirty(false);
    } catch {
      toast.error('Failed to load week');
    }
  };

  const handleWeekSelect = (e) => {
    const id = e.target.value;
    setSelectedWeekId(id);
    if (id) {
      loadWeek(id);
    } else {
      // New blank week
      setCurrentId(null);
      setWeekStart(getMonday());
      setProjectManager('');
      setMinGoal('');
      setRows([emptyRow(), emptyRow(), emptyRow()]);
      setDirty(false);
    }
  };

  const handleRowChange = (idx, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
    setDirty(true);
  };

  const addRow = () => {
    setRows(prev => [...prev, emptyRow()]);
    setDirty(true);
  };

  const removeRow = (idx) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const weekLabel = getWeekLabel(weekStart);

  // Summary calcs
  const totalGross = rows.reduce((s, r) => s + fmt(r.amount_to_collect), 0);
  const totalCollected = rows.reduce((s, r) => s + fmt(r.collected), 0);
  const totalAfterFees = rows.reduce((s, r) => s + fmt(r.total_after_fees), 0);

  const byMethod = FINANCE_METHODS.reduce((acc, m) => {
    acc[m] = rows.filter(r => r.finance_method === m).reduce((s, r) => s + fmt(r.amount_to_collect), 0);
    return acc;
  }, {});

  const handleSave = async () => {
    if (!weekStart) { toast.error('Please set a week start date'); return; }
    setSaving(true);
    try {
      const payload = { week_label: weekLabel, week_start: weekStart, project_manager: projectManager, min_goal: fmt(minGoal), rows };
      if (currentId) {
        await api.put(`/collection/${currentId}`, payload);
        toast.success('Week updated!');
        await loadWeekList();
        setSelectedWeekId(String(currentId));
      } else {
        const res = await api.post('/collection', payload);
        const newId = res.data ? res.data.id : res.id;
        setCurrentId(newId);
        await loadWeekList();
        setSelectedWeekId(String(newId));
        toast.success('Week saved!');
      }
      setDirty(false);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentId) return;
    if (!window.confirm('Delete this week\'s record? This cannot be undone.')) return;
    try {
      await api.delete(`/collection/${currentId}`);
      toast.success('Week deleted');
      setCurrentId(null);
      setSelectedWeekId('');
      setWeekStart(getMonday());
      setProjectManager('');
      setMinGoal('');
      setRows([emptyRow(), emptyRow(), emptyRow()]);
      loadWeekList();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const currency = (n) => n === 0 ? '—' : '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div style={{ padding: '24px', maxWidth: '100%', fontFamily: 'DM Sans, sans-serif' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontFamily: 'DM Serif Display, serif', color: '#0d1f3b' }}>
            Collection Report
          </h1>
          <p style={{ margin: '4px 0 0', color: '#555', fontSize: 14 }}>
            {currentId ? `Editing: ${weekLabel}` : `New week: ${weekLabel}`}
            {dirty && <span style={{ color: '#F57F17', marginLeft: 8, fontWeight: 600 }}>● Unsaved changes</span>}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Load past week */}
          <select
            value={selectedWeekId}
            onChange={handleWeekSelect}
            style={selectStyle}
          >
            <option value="">+ New Week</option>
            {weeks.map(w => (
              <option key={w.id} value={w.id}>{w.week_label} {w.project_manager ? `— ${w.project_manager}` : ''}</option>
            ))}
          </select>

          {currentId && (
            <button onClick={handleDelete} style={{ ...btnStyle, background: '#c62828', color: '#fff' }}>
              Delete Week
            </button>
          )}

          <button onClick={handleSave} disabled={saving} style={{ ...btnStyle, background: '#1a355f', color: '#fff' }}>
            {saving ? 'Saving…' : currentId ? 'Update Week' : 'Save Week'}
          </button>
        </div>
      </div>

      {/* Week meta */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <label style={labelStyle}>Week Start (Monday)</label>
          <input
            type="date"
            value={weekStart}
            onChange={e => { setWeekStart(e.target.value); setDirty(true); }}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Project Manager</label>
          <input
            value={projectManager}
            onChange={e => { setProjectManager(e.target.value); setDirty(true); }}
            placeholder="e.g. Ryan M"
            style={{ ...inputStyle, width: 160 }}
          />
        </div>
        <div>
          <label style={labelStyle}>Minimum Collection Net Goal ($)</label>
          <input
            type="number"
            value={minGoal}
            onChange={e => { setMinGoal(e.target.value); setDirty(true); }}
            placeholder="0"
            style={{ ...inputStyle, width: 160 }}
          />
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <SummaryCard label="Total to Collect" value={currency(totalGross)} color="#0d1f3b" />
        <SummaryCard label="Total Collected" value={currency(totalCollected)} color="#1a355f" />
        <SummaryCard label="After Fees" value={currency(totalAfterFees)} color="#2d4c7e" />
        <SummaryCard label="Min Goal" value={minGoal ? currency(fmt(minGoal)) : '—'} color="#F57F17" />
      </div>

      {/* Finance method breakdown */}
      <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '12px 16px', marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Amount to Collect by Finance Method</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {FINANCE_METHODS.map(m => (
            <div key={m} style={{ fontSize: 13, color: byMethod[m] > 0 ? '#0d1f3b' : '#aaa' }}>
              <span style={{ fontWeight: 700 }}>{m}</span>{' '}
              <span>{byMethod[m] > 0 ? currency(byMethod[m]) : '—'}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Jobs table */}
      <div style={{ overflowX: 'auto', marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 1200 }}>
          <thead>
            <tr style={{ background: '#0d1f3b', color: '#fff' }}>
              <th style={th}>#</th>
              <th style={th}>Customer Name</th>
              <th style={th}>Amt to Collect</th>
              <th style={th}>Product</th>
              <th style={th}>Finance Method</th>
              <th style={th}>Est Collection Date</th>
              <th style={th}>Collected</th>
              <th style={th}>Date Collected</th>
              <th style={th}>Payment Type</th>
              <th style={th}>File Submitted</th>
              <th style={th}>File Date</th>
              <th style={th}>Total After Fees</th>
              <th style={th}>Date in Bank</th>
              <th style={th}>Sales Rep</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                <td style={td}>{idx + 1}</td>
                <td style={td}><input value={row.customer_name} onChange={e => handleRowChange(idx, 'customer_name', e.target.value)} style={cellInput} placeholder="Customer name" /></td>
                <td style={td}><input type="number" value={row.amount_to_collect} onChange={e => handleRowChange(idx, 'amount_to_collect', e.target.value)} style={{ ...cellInput, width: 90 }} placeholder="0" /></td>
                <td style={td}>
                  <select value={row.product} onChange={e => handleRowChange(idx, 'product', e.target.value)} style={cellSelect}>
                    <option value="">—</option>
                    {PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </td>
                <td style={td}>
                  <select value={row.finance_method} onChange={e => handleRowChange(idx, 'finance_method', e.target.value)} style={cellSelect}>
                    <option value="">—</option>
                    {FINANCE_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </td>
                <td style={td}><input type="date" value={row.est_collection_date} onChange={e => handleRowChange(idx, 'est_collection_date', e.target.value)} style={{ ...cellInput, width: 130 }} /></td>
                <td style={td}><input type="number" value={row.collected} onChange={e => handleRowChange(idx, 'collected', e.target.value)} style={{ ...cellInput, width: 90, background: row.collected ? '#e8edf5' : undefined }} placeholder="0" /></td>
                <td style={td}><input type="date" value={row.collected_date} onChange={e => handleRowChange(idx, 'collected_date', e.target.value)} style={{ ...cellInput, width: 130 }} /></td>
                <td style={td}><input value={row.payment_type} onChange={e => handleRowChange(idx, 'payment_type', e.target.value)} style={{ ...cellInput, width: 100 }} placeholder="Type" /></td>
                <td style={td}>
                  <select value={row.file_submitted} onChange={e => handleRowChange(idx, 'file_submitted', e.target.value)} style={cellSelect}>
                    <option value="">—</option>
                    <option value="YES">YES</option>
                    <option value="NO">NO</option>
                  </select>
                </td>
                <td style={td}><input type="date" value={row.file_submitted_date} onChange={e => handleRowChange(idx, 'file_submitted_date', e.target.value)} style={{ ...cellInput, width: 130 }} /></td>
                <td style={td}><input type="number" value={row.total_after_fees} onChange={e => handleRowChange(idx, 'total_after_fees', e.target.value)} style={{ ...cellInput, width: 100 }} placeholder="0" /></td>
                <td style={td}><input type="date" value={row.date_in_bank} onChange={e => handleRowChange(idx, 'date_in_bank', e.target.value)} style={{ ...cellInput, width: 130 }} /></td>
                <td style={td}><input value={row.sales_rep} onChange={e => handleRowChange(idx, 'sales_rep', e.target.value)} style={{ ...cellInput, width: 90 }} placeholder="Rep" /></td>
                <td style={td}>
                  <button onClick={() => removeRow(idx)} style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: 16, padding: '0 4px' }} title="Remove row">✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={addRow} style={{ ...btnStyle, background: '#1f314e', color: '#fff' }}>
          + Add Row
        </button>
        <button onClick={handleSave} disabled={saving} style={{ ...btnStyle, background: '#F57F17', color: '#fff' }}>
          {saving ? 'Saving…' : currentId ? 'Update Week' : 'Save Week'}
        </button>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{ background: '#fff', border: `2px solid ${color}`, borderRadius: 8, padding: '10px 18px', minWidth: 160 }}>
      <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

// Styles
const th = { padding: '10px 8px', textAlign: 'left', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.15)' };
const td = { padding: '4px 4px', borderBottom: '1px solid #eee', verticalAlign: 'middle' };
const cellInput = { border: '1px solid #ddd', borderRadius: 4, padding: '4px 6px', fontSize: 13, width: '100%', minWidth: 80, background: 'transparent', outline: 'none', fontFamily: 'DM Sans, sans-serif' };
const cellSelect = { border: '1px solid #ddd', borderRadius: 4, padding: '4px 6px', fontSize: 13, background: 'transparent', outline: 'none', fontFamily: 'DM Sans, sans-serif' };
const inputStyle = { border: '1px solid #ccc', borderRadius: 6, padding: '7px 10px', fontSize: 14, outline: 'none', fontFamily: 'DM Sans, sans-serif' };
const selectStyle = { border: '1px solid #ccc', borderRadius: 6, padding: '7px 10px', fontSize: 14, outline: 'none', background: '#fff', fontFamily: 'DM Sans, sans-serif', minWidth: 220 };
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };
const btnStyle = { padding: '8px 18px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: 'DM Sans, sans-serif' };
